import $               from 'jquery';
import Step            from './step';
import { createStore } from '../redux-lite';
import extend          from '../util/extend';
import EventEmitter    from './event-emitter';
import template        from '../templates/form-sequence.hbs';

// default values
const defaults = {

  // default FormSequence template
  template,

  titleSelector: '.sequence-step-title',

  descriptionSelector: '.sequence-step-description',

  footerSelector: '.sequence-step-footer',

  // step container selector
  stepSelector: '.sequence-step',

  // controls selector
  controlsSelector: '.sequence-controls',

  // prev button selector
  prevSelector: '.sequence-prev',

  // next button selector
  nextSelector: '.sequence-next',

  // submit button selector
  submitSelector: '.sequence-submit',

  // help button selector
  helpButtonSelector: '.sequence-help',

  // hidden prev/next button class
  hiddenClass: 'hidden',

  // disabled prev/next button class
  disabledClass: 'disabled',

  // name of current step
  currStep: null,

  // step history
  history: []

};

/**
 * FormSequence class
 */

export default class FormSequence extends EventEmitter {

  /**
   * FormStep constructor
   *
   * Must be initialized with:
   *
   * $el     – a root jQuery object containing an HTMLFormElement
   * configs – an Object of Step configs in the format:
   *
   * {
   *   [ step name ]: {
   *     // reference to a Step instance
   *     step: <Step instance>,
   *     // name of next step, function that returns a name, or null
   *     // when there is no next step
   *     next: <String|Function|null>
   *   }
   * }
   *
   * @param  {Object} config – configuration data
   * @return {FormSequence}
   */

  constructor( config ) {
    super();

    extend( this, defaults, config );

    // ensure jQuery root exists
    if ( !( this.$el instanceof $ ) ) {
      const msg = `this.$el must be a jQuery object, got ${ this.$el }`;
      throw new TypeError( msg );
    }

    // ensure configs map exists
    if ( !this.configs || typeof this.configs !== 'object' ) {
      const msg = `this.configs must be an object, got ${ this.configs }`;
      throw new TypeError( msg );
    }

    // ensure name of first step exists
    if ( typeof this.first !== 'string' ) {
      const msg = `this.first must be a step name, got ${ this.configs }`;
      throw new TypeError( msg );
    }

    // validate configs in this.configs
    Object.keys( this.configs ).forEach( name => {
      const config   = this.configs[ name ];
      const nextType = typeof config.next;

      if ( !( config.step instanceof Step ) ) {
        const msg = `Invalid step ${ config.step }`;
        throw new TypeError( msg );
      }
      if ( config.next !== null && !/string|function/.test( nextType ) ) {
        const msg = `Invalid next ${ config.next }`;
        throw new TypeError( msg );
      }
    });

    this.store = createStore( this.reducer.bind( this ), this.state || {} );

    this.bindEvents();
    this.render();
    this.showStep( this.first );
  }

  /**
   * Redux reducer
   *
   * @param  {Object} state  – previous state
   * @param  {Object} action – action
   * @return {Object}        – new state
   */

  reducer( state, action ) {
    const result = Object.assign( {}, state );

    if ( action.type === 'change' ) {
      Object.keys( action.data ).forEach( key => {
        const props  = key.split('.');
        const last   = props.pop();
        let target = result;

        props.forEach( prop => {
          target = target[ prop ] || ( target[ prop ] = {} );
        });

        const value = action.data[ key ];

        if ( value === undefined ) {
          delete target[ last ];
        } else {
          target[ last ] = action.data[ key ];
        }
      });
    }

    return result;
  }

  /**
   * Bind events
   *
   * Subscribe to Action dispatches, store changes, and click events
   *
   * @return {FormStep}
   */

  bindEvents() {
    const names = Object.keys( this.configs );
    const steps = names.map( name => this.configs[ name ].step );

    steps.forEach( step => step.on( 'dispatch', this.store.dispatch ) );
    steps.forEach( step => step.on( 'submit', this.stepSubmit.bind( this ) ) );

    this.store.subscribe( this.updateButtonStates.bind( this ) );

    this.$el.on( 'click', this.prevSelector, this.onPrev.bind( this ) );
    this.$el.on( 'click', this.nextSelector, this.onNext.bind( this ) );
    this.$el.on( 'click', this.submitSelector, this.onSubmit.bind( this ) );

    if ( typeof this.events === 'object' ) {
      const events = this.events;
      Object.keys( events ).forEach( event => {
        this.on( event, ( ...args ) => this[ events[ event ] ]( ...args ) );
      });
    }

    return this;
  }

  /**
   * Render the sequence
   *
   * @return {FormSequence}
   */

  render() {
    this.$el.html( this.template() );
    return this;
  }

  /**
   * Update button states
   *
   * Set visibility and enabled/disabled state of prev/next buttons
   *
   * @param  {Object} state – redux store state
   * @return {FormSequence}
   */

  updateButtonStates( state ) {
    const conf     = this.configs[ this.currStep ];
    const isValid  = conf.step.isValid( state );
    const isFirst  = this.history.length === 0;
    const showHelp = state.showHelp;

    let isLast = false;

    if ( conf.next === null ||
      ( typeof conf.next === 'function' && conf.next( state ) === null ) ) {
      isLast = true;
    }

    const $title    = this.$el.find( this.titleSelector );
    const $desc     = this.$el.find( this.descriptionSelector );
    const $controls = this.$el.find( this.controlsSelector );
    const $footer   = this.$el.find( this.footerSelector );
    const $prev     = this.$el.find( this.prevSelector );
    const $next     = this.$el.find( this.nextSelector );
    const $submit   = this.$el.find( this.submitSelector );
    const $help     = this.$el.find( this.helpButtonSelector );

    $title.text( conf.title || '' );
    $desc.html( conf.description || '' );
    $footer.html( conf.footer || '' );

    $prev.toggleClass( this.hiddenClass, isFirst );

    $next.toggleClass( this.hiddenClass, isLast );
    $next.toggleClass( this.disabledClass, !isValid );

    $submit.toggleClass( this.hiddenClass, !isLast );
    $submit.toggleClass( this.disabledClass, !isValid );

    $help.toggleClass( this.hiddenClass, !showHelp );

    if ( conf.nextText ) {
      $next.find('.label').text( conf.nextText );
    } else {
      $next.find('.label').text('Next');
    }

    if ( conf.hideControls ) {
      $controls.hide();
    } else {
      $controls.show();
    }

    return this;
  }

  /**
   * Show the step at the given index
   *
   * @param  {String}  name   – step name
   * @param  {Boolean} isBack – is this result of navigating backward?
   * @param  {Object}  errors - Key/value pairs, where key is the input 'name',
   *                            and value is error message to be shown in UI
   * @return {FormSequence}
   */

  showStep( name, isBack = false, opts ) {
    const $container = this.$el.find( this.stepSelector );
    const newStep    = this.configs[ name ].step;

    const state = Object.assign( {}, this.store.getState(), opts );

    if ( this.currStep ) {
      const oldStep = this.configs[ this.currStep ].step;
      oldStep.$el.detach();
      if ( !isBack ) {
        this.history.push( this.currStep );
      }
    }

    this.emit( 'showStep', name, newStep );

    this.currStep = name;

    newStep.render( state );
    $container.append( newStep.$el );
    newStep.emit('afterRender');

    // scroll back to top of next step
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const $autofocus = $container.find('input[autofocus]');

    // respect autofocus on desktop
    if ( !( 'ontouchstart' in window ) ) {
      $autofocus.focus();
    }

    // put cursor at the end of the text
    $autofocus.val( $autofocus.val() );

    this.updateButtonStates( state );
    this.updateErrorStates( state.errors );

    return this;
  }

  /**
   * Show the previous step
   *
   * No-ops if the button was disabled
   *
   * @param  {Event} e – DOM event
   * @return {Undefined}
   */

  onPrev( e ) {
    if ( !$( e.target ).hasClass( this.disabledClass ) ) {
      this.showStep( this.history.pop(), true );
    }
  }

  /**
   * Show the next step
   *
   * No-ops if the button was disabled
   *
   * @param  {Event} e – DOM event
   * @return {Undefined}
   */

  onNext( e ) {
    const conf = this.configs[ this.currStep ];
    const state = this.store.getState();

    if ( !$( e.target ).hasClass( this.disabledClass ) ) {
      const before = conf.step.beforeNext || function( state, f ) { f(); };

      conf.step.emit( 'submit-clicked', state );

      before.call( conf.step, state, () => {
        if ( typeof conf.next === 'string' ) {
          this.showStep( conf.next );
        } else if ( typeof conf.next === 'function' ) {
          this.showStep( conf.next( state ) );
        }
      });

    } else {
      conf.step.isValid( state, true );
    }
  }

  /**
   * Call submit
   *
   * No-ops if the button was disabled
   *
   * @param  {Event} e – DOM event
   * @return {Undefined}
   */

  onSubmit( e ) {
    const conf = this.configs[ this.currStep ];
    const state = this.store.getState();

    if ( !$( e.target ).hasClass( this.disabledClass ) ) {
      const before = conf.step.beforeNext || function( state, f ) { f(); };

      conf.step.emit( 'submit-clicked', state );

      before.call( conf.step, state, this.submit.bind( this ) );
    } else {
      conf.step.isValid( state, true );
    }
  }

  /**
   * Intercept a submit event from a step
   *
   * @return {undefined}
   */

  stepSubmit() {
    const $submit = this.$el
    .find( this.submitSelector )
    .not( `.${ this.hiddenClass }` );

    const $next = this.$el
    .find( this.nextSelector )
    .not( `.${ this.hiddenClass }` );

    if ( $submit.length ) {
      $submit.trigger('click');
    } else if ( $next.length ) {
      $next.trigger('click');
    }
  }

  /**
   * Submit
   *
   * May be overridden, otherwise just emits 'submit' event with redux state
   *
   * @return {FormSequence}
   */

  submit() {
    const state = this.store.getState();
    return this.emit( 'submit', state );
  }

  /**
   * Add error classes to input elements when preloading a form step with errors
   *
   * @param {Object} errors - key/value pairs, where the key should match the
   *                          name of an input element that the 'error' class
   *                          will be appended to.
   * @return {undefined}
   */
  updateErrorStates( errors ) {
    for ( const name in errors ) {
      const $input = this.$el.find( `input[name="${ name }"]` );
      $input.addClass('error');
    }
  }

}
